// frontend/src/components/EditStatement.js
import React, { useState } from 'react';
import axios from '../api';
import Loader from './Loader';

function EditStatement({ data }) {
  // Metadata states
  const [statementDate, setStatementDate] = useState(data.statement_date || '');
  const [dueDate, setDueDate] = useState(data.due_date || '');
  const [billingCycle, setBillingCycle] = useState(data.billing_cycle || '');
  const [gracePeriod, setGracePeriod] = useState(data.grace_period || '');
  const [totalDue, setTotalDue] = useState(data.total_due || '');
  const [issuer, setIssuer] = useState(data.issuer || 'Unknown');

  // Transactions state
  const [transactions, setTransactions] = useState(data.transactions || []);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');

  const handleTransactionChange = (index, field, value) => {
    const updated = [...transactions];
    updated[index][field] = value;
    setTransactions(updated);
  };

  const handleExport = async (format) => {
    if (!transactions || transactions.length === 0) {
      alert("No transactions available to export.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        issuer: issuer,
        statement_date: statementDate,
        due_date: dueDate,
        billing_cycle: billingCycle,
        grace_period: gracePeriod,
        total_due: totalDue,
        transactions,
        format
      };

      const res = await axios.post('/export', payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      setTimeout(() => {
        // Create downloadable file
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `statement.${format === 'excel' ? 'xlsx' : format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setLoading(false);
      }, 1500);

    } catch (err) {
      setLoading(false);
      console.error("Export error:", err);
      const errMsg = err.response?.data?.error || err.message;
      alert(`Export failed: ${errMsg}`);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="card" style={{ marginTop: '2rem' }}>
      {/* Header */}
      <h2 style={{ 
        fontSize: '1.8rem',
        marginBottom: '2rem',
        background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }}>
        Edit Statement Details
      </h2>

      {/* Metadata Section */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div className="input-group">
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Card Issuer:
          </label>
          <input
            type="text"
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>

        <div className="input-group">
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Statement Date:
          </label>
          <input
            type="text"
            value={statementDate}
            onChange={(e) => setStatementDate(e.target.value)}
            placeholder="YYYY-MM-DD"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>

        <div className="input-group">
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Payment Due Date:
          </label>
          <input
            type="text"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            placeholder="YYYY-MM-DD"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>

        <div className="input-group">
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Billing Cycle:
          </label>
          <input
            type="text"
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value)}
            placeholder="e.g., 01 Jan - 31 Jan"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>

        <div className="input-group">
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Grace Period:
          </label>
          <input
            type="text"
            value={gracePeriod}
            onChange={(e) => setGracePeriod(e.target.value)}
            placeholder="e.g., 15 days"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>

        <div className="input-group">
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Total Due:
          </label>
          <input
            type="text"
            value={totalDue}
            onChange={(e) => setTotalDue(e.target.value)}
            placeholder="e.g., $1,234.56"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '1rem'
            }}
          />
        </div>
      </div>

      {/* Transactions Section */}
      <h3 style={{ 
        fontSize: '1.5rem',
        marginBottom: '1.5rem',
        color: '#00ffff',
        fontWeight: '700'
      }}>
        Transactions
      </h3>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((txn, index) => (
              <tr key={index}>
                <td>
                  <input
                    type="text"
                    value={txn.date || ''}
                    onChange={(e) => handleTransactionChange(index, 'date', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={txn.description || ''}
                    onChange={(e) => handleTransactionChange(index, 'description', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={txn.amount || ''}
                    onChange={(e) => handleTransactionChange(index, 'amount', e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{ 
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'rgba(0, 255, 255, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(0, 255, 255, 0.2)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          fontSize: '1.1rem',
          fontWeight: '600'
        }}>
          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Total Transactions:
          </span>
          <span style={{ color: '#00ffff' }}>
            {transactions.length}
          </span>
        </div>
      </div>

      {/* Export Buttons */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: '1rem', 
        marginTop: '2rem',
        justifyContent: 'center'
      }}>
        <button onClick={() => handleExport('csv')} className="neon-button">
          <span className="text">📄 Export CSV</span>
        </button>
        <button onClick={() => handleExport('excel')} className="neon-button">
          <span className="text">📊 Export Excel</span>
        </button>
        <button onClick={() => handleExport('json')} className="neon-button">
          <span className="text">🔧 Export JSON</span>
        </button>
        <button onClick={() => handleExport('xml')} className="neon-button">
          <span className="text">📋 Export XML</span>
        </button>
      </div>
    </div>
  );
}

export default EditStatement;